import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Clock, LogIn, LogOut, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import VenturaLogo from "@/components/VenturaLogo";

/** Vista de demostración (sin token real) del picado por WhatsApp. */
const EJEMPLO = {
  nombre: "María Ejemplo",
  empresa: "Empresa Demo",
  centro: "Oficina central",
  fecha_txt: "jueves, 1 de octubre de 2026",
  entrada_propuesta: "09:00",
  salida_propuesta: "18:00",
  siguiente: "entrada",
  siguiente_label: "Entrada",
  hora_sugerida: "09:00",
  hora_registro: "09:02",
  puede_actuar: true,
  ya_tiene_fichajes: false,
  tiene_entrada: false,
  tiene_salida: false,
  entrada_registrada: null,
  salida_registrada: null,
  caducado: false,
};

export default function EjemploFichajeWhatsApp() {
  const [searchParams] = useSearchParams();
  const modoPicado = searchParams.get("modo") === "manual" ? "manual" : "automatico";
  const [vista, setVista] = useState(searchParams.get("vista") === "ok" ? "ok" : "resumen");
  const [hora, setHora] = useState(EJEMPLO.hora_sugerida);

  const data = useMemo(
    () => ({
      ...EJEMPLO,
      modo_picado: modoPicado,
      ...(vista === "ok"
        ? {
            entrada_registrada: modoPicado === "manual" ? hora : "09:02",
            tiene_entrada: true,
            mensaje_confirmacion: "Entrada registrada (ejemplo). En producción quedaría en tu ficha.",
            siguiente: "salida",
            siguiente_label: "Salida",
            hora_sugerida: "18:00",
          }
        : {}),
    }),
    [modoPicado, vista, hora]
  );

  const SiguienteIcon = data.siguiente === "salida" ? LogOut : LogIn;
  const labelAccion = data.siguiente_label || "picado";

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-fuchsia-50/40 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs text-amber-900">
          <strong>Ejemplo de demostración</strong> · Así ve el trabajador el enlace que llega por WhatsApp.
          {" · "}
          <a href="?modo=automatico" className="underline">
            Automático
          </a>
          {" · "}
          <a href="?modo=manual" className="underline">
            Manual
          </a>
        </div>

        <div className="mb-6 text-center">
          <VenturaLogo className="mx-auto h-12 w-auto" />
          <p className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-violet-700/80">
            Registro de jornada
          </p>
          <h1 className="mt-1 font-display text-2xl font-semibold tracking-tight text-zinc-950">
            {vista === "ok" ? "Confirmación" : "Tu jornada"}
          </h1>
          <p className="mt-1 text-sm font-medium text-zinc-800">{data.nombre}</p>
          <p className="mt-0.5 text-sm text-zinc-500">
            {[data.empresa, data.centro].filter(Boolean).join(" · ")}
          </p>
        </div>

        <div className="rounded-2xl border border-violet-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-violet-800">
              <Clock className="h-4 w-4" />
              <span className="text-sm font-medium">{data.fecha_txt}</span>
            </div>
            <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-medium text-violet-700">
              {data.entrada_propuesta} – {data.salida_propuesta}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <div
              className={`rounded-xl border px-3 py-2 text-center ${
                data.tiene_entrada
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-violet-300 bg-violet-50 text-violet-900 ring-1 ring-violet-200"
              }`}
            >
              <LogIn className="mx-auto h-4 w-4" />
              <p className="mt-1 text-[11px] font-medium">Entrada</p>
              <p className="text-sm tabular-nums font-semibold">
                {data.entrada_registrada || data.hora_sugerida}
              </p>
              {data.tiene_entrada && <p className="text-[10px] text-emerald-700">Hecha</p>}
            </div>
            <div className="rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2 text-center text-zinc-400">
              <LogOut className="mx-auto h-4 w-4" />
              <p className="mt-1 text-[11px] font-medium">Salida</p>
              <p className="text-sm tabular-nums font-semibold">{data.salida_propuesta}</p>
              <p className="text-[10px]">Después</p>
            </div>
          </div>

          {vista === "ok" ? (
            <div className="mt-6 space-y-3 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-violet-100 text-violet-700">
                <Check className="h-6 w-6" />
              </div>
              <p className="font-medium text-zinc-900">Registro confirmado</p>
              <p className="text-sm text-zinc-600">{data.mensaje_confirmacion}</p>
              <div className="mt-3 rounded-xl bg-violet-50 px-4 py-3 text-sm text-violet-950">
                <p>
                  Entrada: <strong className="tabular-nums">{data.entrada_registrada}</strong>
                </p>
              </div>
              <Button
                className="mt-2 w-full bg-violet-600 hover:bg-violet-700"
                onClick={() => setVista("resumen")}
              >
                Volver al ejemplo
              </Button>
            </div>
          ) : vista === "modificar" ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3">
                <p className="text-xs text-violet-700/80">Picado manual</p>
                <p className="mt-0.5 text-sm font-medium text-violet-950">
                  Selecciona la hora de <strong>{labelAccion.toLowerCase()}</strong>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Hora de {labelAccion.toLowerCase()}</Label>
                <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 border-violet-200" onClick={() => setVista("resumen")}>
                  Atrás
                </Button>
                <Button className="flex-1 bg-violet-600 hover:bg-violet-700" onClick={() => setVista("ok")}>
                  Guardar picado
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-5">
              <div className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50 to-fuchsia-50 px-4 py-4 text-center">
                <p className="text-xs text-violet-700/80">Siguiente picado</p>
                <div className="mt-2 flex items-center justify-center gap-2 text-violet-950">
                  <SiguienteIcon className="h-5 w-5" />
                  <span className="text-lg font-semibold">{labelAccion}</span>
                </div>
                {modoPicado === "manual" ? (
                  <>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-violet-950">{data.hora_sugerida}</p>
                    <p className="mt-1 text-xs text-zinc-500">Hora prevista según tu horario</p>
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-2xl font-semibold tabular-nums text-violet-950">{data.hora_registro}</p>
                    <p className="mt-1 text-xs text-zinc-500">
                      Se registrará la hora exacta al pulsar (horario previsto {data.hora_sugerida})
                    </p>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button className="w-full bg-violet-600 hover:bg-violet-700" onClick={() => setVista("ok")}>
                  <SiguienteIcon className="mr-2 h-4 w-4" />
                  Registrar {labelAccion.toLowerCase()}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-violet-200 text-violet-900"
                  onClick={() => setVista("modificar")}
                >
                  <Pencil className="mr-2 h-4 w-4" /> Modificar hora
                </Button>
              </div>
              <p className="text-center text-[11px] text-zinc-400">
                {modoPicado === "manual"
                  ? "«Registrar» usa la hora prevista. «Modificar hora» permite elegirla a mano."
                  : "«Registrar» usa la hora real ahora. «Modificar hora» permite elegir otra hora."}
              </p>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-zinc-400">
          Ventura HR · Enlace real: <code className="rounded bg-zinc-100 px-1">/wa/jornada/TOKEN</code>
        </p>
      </div>
    </div>
  );
}
